import { QuadraticBezierCurve3, Vector3 } from 'three';
import { resolveVisibleConnectorIntervals } from './connectorLineGaps.js';
import { validateConnectorOrbit } from './connectorOrbit.js';
import { resolveConnectorEndpoint } from './entityAnchorResolution.js';
import { resolveSidePairAnchorEndpoint } from './sidePairAnchorResolution.js';
import { isVisibleByPresentation, resolveConnectorBracketPoints, resolveConnectorColor, resolveConnectorCurveControlPoint, resolveConnectorCurveDirection, resolveConnectorCurveMinOffset, resolveConnectorCurvePlane, resolveConnectorCurveStrength, resolveConnectorOpacity, resolveConnectorPathKind, resolveConnectorProgressiveActivation, } from './spaceRendererHelpers.js';
export function resolveConnectorItemRenderPlan(item, context) {
    const state = context.anchors.itemParametricStateByKey?.[item.itemKey];
    const endpoint = (role) => resolveConnectorEndpoint({
        endpoint: resolveSidePairAnchorEndpoint({
            parametricState: state, endpointRole: role, endpoint: item.endpoints[role], context: context.anchors,
        }),
        context: context.anchors,
        positionOverride: state?.endpointPositions?.[role],
    });
    const from = endpoint('from');
    const to = endpoint('to');
    if (!from || !to)
        return null;
    const start = from.position;
    const end = to.position;
    const pathKind = resolveConnectorPathKind(state);
    const color = resolveConnectorColor(state, context.effectiveActiveItemKeyByConstellationKey, context.effectiveLinkedActiveItemKeysByConstellationKey);
    const opacity = resolveConnectorOpacity(state);
    const thickness = state?.thickness === undefined ? 0 : state.thickness;
    if (!Number.isFinite(thickness) || thickness < 0) {
        throw new Error(`Connector ${item.itemKey} has an invalid thickness.`);
    }
    const strokeCount = state?.strokeCount ?? 1;
    if (!Number.isInteger(strokeCount) || strokeCount < 1 || strokeCount > 4) {
        throw new Error(`Connector ${item.itemKey} has an invalid stroke count.`);
    }
    const strokeSpacing = state?.strokeSpacing;
    if (strokeSpacing !== undefined && (!Number.isFinite(strokeSpacing) || strokeSpacing <= 0)) {
        throw new Error(`Connector ${item.itemKey} has an invalid stroke spacing.`);
    }
    const innerStrokePattern = state?.innerStrokePattern;
    if (innerStrokePattern !== undefined && innerStrokePattern !== 'dashed') {
        throw new Error(`Connector ${item.itemKey} has an invalid inner stroke pattern.`);
    }
    if ((strokeCount > 1 || strokeSpacing !== undefined || innerStrokePattern !== undefined) && thickness <= 0) {
        throw new Error(`Connector ${item.itemKey} stroke styling requires a thick connector.`);
    }
    const line = { start, end, color, opacity };
    const activation = pathKind === 'straight'
        ? resolveConnectorProgressiveActivation(state, start, end) : state?.progressiveActivation;
    const intervals = pathKind === 'straight' && state?.lineGaps
        ? resolveVisibleConnectorIntervals({ start, end, gaps: state.lineGaps, context: context.anchors }) : undefined;
    const depthTest = state?.depthTest ?? true;
    let presentation;
    if (thickness > 0) {
        if (pathKind !== 'straight' || state?.endpointOrbit || activation || state?.lineGaps
            || state?.cameraNearEyeClipRadius !== undefined) {
            throw new Error(`Thick connector ${item.itemKey} requires a static straight path.`);
        }
        presentation = {
            kind: 'tube',
            ...line,
            thickness,
            depthTest,
            ...(strokeCount > 1 ? { strokeCount } : {}),
            ...(strokeSpacing !== undefined ? { strokeSpacing } : {}),
            ...(innerStrokePattern !== undefined ? { innerStrokePattern } : {}),
        };
    }
    else if (state?.endpointOrbit) {
        validateConnectorOrbit(state.endpointOrbit);
        if (pathKind !== 'straight' || activation || state.lineGaps || state.cameraNearEyeClipRadius !== undefined) {
            throw new Error('Connector orbit requires a straight guide without activation, gaps or near-eye clipping.');
        }
        presentation = { kind: 'orbit', ...line, orbit: state.endpointOrbit, depthTest };
    }
    else if (intervals && !(activation?.ripple && activation.ripple.amplitude > 0)) {
        presentation = { kind: 'gapped', ...line, intervals, depthTest, activation };
    }
    else if (pathKind === 'straight' && activation != null) {
        if (activation.ripple && activation.ripple.amplitude > 0) {
            presentation = { kind: 'ripple', ...line, intervals, depthTest, activation };
        }
        else {
            const activeStart = activation.direction === 'to-from' ? end : start;
            const activeEnd = activation.direction === 'to-from' ? start : end;
            const progress = Math.max(0, Math.min(1, activation.progress01));
            const activeTip = [
                activeStart[0] + (activeEnd[0] - activeStart[0]) * progress,
                activeStart[1] + (activeEnd[1] - activeStart[1]) * progress,
                activeStart[2] + (activeEnd[2] - activeStart[2]) * progress,
            ];
            presentation = { kind: 'activation', ...line,
                overlay: progress > 0 && opacity > 0
                    ? { start: activeStart, end: activeTip, color: activation.color, opacity } : null };
        }
    }
    else if (pathKind === 'straight' && state?.cameraNearEyeClipRadius !== undefined) {
        presentation = { kind: 'near-eye-clipped', ...line, radius: state.cameraNearEyeClipRadius };
    }
    else if (pathKind === 'bracket') {
        const points = resolveConnectorBracketPoints({ start, end, parametricState: state });
        presentation = activation ? { kind: 'progressive-path', points, color, opacity, depthTest, activation }
            : { kind: 'line', ...line, points };
    }
    else if (pathKind === 'curved') {
        const control = resolveConnectorCurveControlPoint({
            start, end, curvePlane: resolveConnectorCurvePlane(state),
            curveStrength: resolveConnectorCurveStrength(state),
            curveMinOffset: resolveConnectorCurveMinOffset(state),
            curveDirection: resolveConnectorCurveDirection(state),
        });
        presentation = control === null ? { kind: 'line', ...line }
            : activation ? { kind: 'progressive-path', color, opacity, depthTest, activation,
                points: new QuadraticBezierCurve3(new Vector3(...start), new Vector3(...control), new Vector3(...end))
                    .getPoints(24).map(point => point.toArray()) }
                : { kind: 'curve', ...line, control };
    }
    else {
        presentation = { kind: 'line', ...line };
    }
    return { itemKey: item.itemKey, presentation };
}
export function resolveConnectorConstellationRenderPlan(input) {
    const state = input.connectorConstellationParametricStateByKey[input.constellation.constellationKey];
    if (state?.visibility?.visible === false || !isVisibleByPresentation({
        visibility: state?.visibilityRule,
        hoveredEntity: input.hoveredEntity,
        effectiveActiveItemKeyByConstellationKey: input.effectiveActiveItemKeyByConstellationKey,
        effectiveLinkedActiveItemKeysByConstellationKey: input.effectiveLinkedActiveItemKeysByConstellationKey,
    }))
        return null;
    return {
        constellationKey: input.constellation.constellationKey,
        items: input.items.flatMap(item => {
            const plan = resolveConnectorItemRenderPlan(item, input);
            return plan ? [plan] : [];
        }),
    };
}
